import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Save as SaveIcon, Plus, Trash2, Star } from "lucide-react";
import { WorkspaceSectionHeader } from "../workspace-section-header";
import { toast } from "sonner";
import { useCallback, useState } from "react";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import {
  iOrganizationTrustPilotData,
  iOrganizationTrustPilotWidgetPreset,
} from "@/@sdk/brands/brand-model";
import { useBrandById } from "@/hooks/use-brands";

const trustPilotPresetSchema = z.object({
  id: z.string().optional(),
  label: z.string().min(1, "Label is required"),
  businessUnitId: z.string().min(1, "Business Unit ID is required"),
  templateId: z.string().min(1, "Template ID is required"),
  widgetHeight: z.number().min(40, "Height must be at least 40px"),
});

const trustPilotSchema = z.object({
  widgetPresets: z.array(trustPilotPresetSchema),
});

type TrustPilotFormValues = z.infer<typeof trustPilotSchema>;

export function TrustPilotSection({ brandId }: { brandId: string }) {
  const brand = useBrandById(brandId);
  const trustPilotConfig = brand?.trustPilotConfig;
  const [isAddingPreset, setIsAddingPreset] = useState(false);

  const { doAction: editBrand, isProcessing } = useSDKActionWithDeps(
    () => ({
      action:
        (SDK) =>
        (edits: { trustPilotConfig: iOrganizationTrustPilotData }) =>
          SDK.brands.editById(brandId, edits),
      failureMessage: "Failed to update TrustPilot settings",
    }),
    [brandId]
  );

  const form = useForm<TrustPilotFormValues>({
    resolver: zodResolver(trustPilotSchema),
    defaultValues: {
      widgetPresets: trustPilotConfig?.widgetPresets || [],
    },
  });

  const onSubmit = useCallback(
    async (data: TrustPilotFormValues) => {
      try {
        await editBrand({
          trustPilotConfig: {
            widgetPresets: data.widgetPresets,
          },
        });
        toast.success("TrustPilot settings updated successfully");
      } catch (error) {
        toast.error("Failed to update TrustPilot settings");
        console.error(error);
      }
    },
    [editBrand]
  );

  const addPreset = useCallback(() => {
    const newPreset: iOrganizationTrustPilotWidgetPreset = {
      id: `preset_${Date.now()}`,
      label: "",
      businessUnitId: "",
      templateId: "",
      widgetHeight: 240,
    };
    
    const currentPresets = form.getValues("widgetPresets");
    form.setValue("widgetPresets", [...currentPresets, newPreset]);
    setIsAddingPreset(true);
  }, [form]);

  const removePreset = useCallback((index: number) => {
    const currentPresets = form.getValues("widgetPresets");
    const updatedPresets = currentPresets.filter((_, i) => i !== index);
    form.setValue("widgetPresets", updatedPresets);
  }, [form]);

  return (
    <div className="space-y-6 px-4 pb-6">
      <WorkspaceSectionHeader
        icon={Star}
        title="TrustPilot"
        description="Manage TrustPilot widget presets for quote and marketing pages."
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPreset}
            disabled={isProcessing}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Preset
          </Button>
        }
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.watch("widgetPresets").map((preset, index) => (
            <div key={preset.id || index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Widget Preset {index + 1}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePreset(index)}
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`widgetPresets.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preset Label</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter preset label" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name={`widgetPresets.${index}.businessUnitId`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Unit ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your Business Unit ID" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name={`widgetPresets.${index}.templateId`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your Template ID" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name={`widgetPresets.${index}.widgetHeight`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Widget Height (px)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter widget height"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          min={20}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          ))}
          
          {form.watch("widgetPresets").length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No widget presets configured yet.</p>
              <p className="text-sm">Click "Add Preset" to create your first widget preset.</p>
            </div>
          )}
          
          <div className="flex justify-end space-x-2 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
